import React from "react";
import { useState, useMemo, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import { ItemForm } from "./ItemForm.jsx";

export function KaininkasEditor({ kainininkas, setGroups, onRename, setManufacturers }) {
          const [editingName, setEditingName] = useState(false);
          const [nameVal, setNameVal] = useState(kainininkas.name);
          const [showAddGroup, setShowAddGroup] = useState(false);
          const [newGroupName, setNewGroupName] = useState('');
          const [editGroupId, setEditGroupId] = useState(null);
          const [editGroupName, setEditGroupName] = useState('');
          const [showAddItem, setShowAddItem] = useState(null); // {gId, sgId}
          const [newItem, setNewItem] = useState(emptyItem());
          const [editItemId, setEditItemId] = useState(null);
          const [editItem, setEditItem] = useState(null);
          const [editItemLoc, setEditItemLoc] = useState(null); // {gId, sgId}
          const [expanded, setExpanded] = useState({});
          const [groupDiscount, setGroupDiscount] = useState({});
          const [showManufacturers, setShowManufacturers] = useState(false);
          const [newManufacturer, setNewManufacturer] = useState('');
          const [importing, setImporting] = useState(false);
          const importFileRef = useRef(null);

          // Pogrupiai
          const [showAddSubgroupFor, setShowAddSubgroupFor] = useState(null); // gId
          const [newSubgroupName, setNewSubgroupName] = useState('');
          const [editSubgroupLoc, setEditSubgroupLoc] = useState(null); // {gId, sgId}
          const [editSubgroupName, setEditSubgroupName] = useState('');
          const [expandedSubgroups, setExpandedSubgroups] = useState({});

          const groups = kainininkas.groups || [];
          const savedManufacturers = kainininkas.manufacturers || [];
          const autoManufacturers = useMemo(() => {
            const set = new Set();
            groups.forEach(g => {
              (g.items||[]).forEach(it => { if (it.gamintojas && it.gamintojas.trim()) set.add(it.gamintojas.trim()); });
              (g.subgroups||[]).forEach(sg => (sg.items||[]).forEach(it => { if (it.gamintojas && it.gamintojas.trim()) set.add(it.gamintojas.trim()); }));
            });
            return [...set];
          }, [groups]);
          const knownManufacturers = useMemo(() => {
            return [...new Set([...savedManufacturers, ...autoManufacturers])].sort();
          }, [savedManufacturers, autoManufacturers]);

          // Pozicijos visada turi būti pogrupyje — jei kokia grupė dar turi senų pozicijų
          // tiesiai grupės lygyje (be pogrupio), vieną kartą perkeliame jas į "Bendra" pogrupį.
          useEffect(() => {
            const needsMigration = groups.some(g => (g.items||[]).length > 0);
            if (!needsMigration) return;
            setGroups(prev => prev.map(g => {
              const topItems = g.items || [];
              if (topItems.length === 0) return g;
              let subgroups = [...(g.subgroups||[])];
              let bendra = subgroups.find(sg => sg.name.trim().toLowerCase() === 'bendra');
              if (!bendra) {
                bendra = { id: Date.now()+Math.random(), name: 'Bendra', items: [] };
                subgroups = [...subgroups, bendra];
              }
              subgroups = subgroups.map(sg => sg.id===bendra.id ? {...sg, items:[...(sg.items||[]), ...topItems]} : sg);
              return {...g, items: [], subgroups};
            }));
          }, [groups]);

          function rememberManufacturer(gamintojas) {
            if (!gamintojas || !gamintojas.trim()) return;
            const g = gamintojas.trim();
            if (!savedManufacturers.includes(g)) {
              setManufacturers(prev => [...prev, g].sort());
            }
          }
          function delManufacturer(g) {
            setManufacturers(prev => prev.filter(x => x !== g));
          }


          function emptyItem() {
            return { name:'', gamintojas:'', unit:'vnt.', qty:1, priceEx:'', pvmPct:21, pvmAmt:'', priceInc:'', priceType:'client', discount:'', personalPrice:'', personalDiscount:'' };
          }

          function r2(v) {
            // Round to 2 decimals: standard half-up rounding
            return Math.round(v * 100) / 100;
          }

          // Randa/pakeičia pozicijų masyvą reikiamoje vietoje: grupės viršuje (sgId=null) arba pogrupyje.
          function withItemsArray(groupsList, gId, sgId, fn) {
            return groupsList.map(g => {
              if (g.id !== gId) return g;
              if (sgId == null) {
                return {...g, items: fn(g.items||[])};
              }
              return {...g, subgroups: (g.subgroups||[]).map(sg => sg.id===sgId ? {...sg, items: fn(sg.items||[])} : sg)};
            });
          }

          function calcItem(item, changedField) {
            let { priceEx, pvmPct, pvmAmt, priceInc, discount, personalPrice, personalDiscount } = item;
            const pvm = (parseFloat(pvmPct)||21) / 100;

            if (changedField === 'priceEx' || changedField === 'pvmPct') {
              const ex = parseFloat(priceEx)||0;
              const amt = r2(ex * pvm);
              const inc = r2(ex + amt);
              return {...item, pvmAmt: amt||'', priceInc: inc||''};
            }
            if (changedField === 'priceInc') {
              const inc = parseFloat(priceInc)||0;
              const ex = r2(inc / (1+pvm));
              const amt = r2(inc - ex);
              return {...item, priceEx: ex||'', pvmAmt: amt||''};
            }
            if (changedField === 'pvmAmt') {
              const amt = parseFloat(pvmAmt)||0;
              const ex = r2(amt / pvm);
              const inc = r2(ex + amt);
              return {...item, priceEx: ex||'', priceInc: inc||''};
            }
            if (changedField === 'discount' || changedField === 'priceType') {
              // If client price known and discount given → calculate personal
              const ex = parseFloat(priceEx)||0;
              const disc = parseFloat(item.discount)||0;
              if (item.priceType === 'client' && disc > 0 && ex > 0) {
                const personal = r2(ex * (1 - disc/100));
                return {...item, personalPrice: personal||''};
              }
            }
            if (changedField === 'personalPrice') {
              const ex = parseFloat(priceEx)||0;
              const personal = parseFloat(personalPrice)||0;
              if (ex > 0 && personal > 0) {
                const disc = r2((1 - personal/ex) * 100);
                return {...item, discount: disc < 0 ? 0 : disc};
              }
            }
            if (changedField === 'personalDiscount') {
              const ex = parseFloat(priceEx)||0;
              const pDisc = parseFloat(personalDiscount)||0;
              if (ex > 0) {
                const personal = r2(ex * (1 - pDisc/100));
                return {...item, personalPrice: personal||''};
              }
            }
            return item;
          }

          function handleNewItemChange(field, value) {
            let updated = {...newItem, [field]: value};
            updated = calcItem(updated, field);
            setNewItem(updated);
          }

          function handleEditItemChange(field, value) {
            let updated = {...editItem, [field]: value};
            updated = calcItem(updated, field);
            setEditItem(updated);
          }

          function addGroup() {
            if (!newGroupName.trim()) return;
            setGroups(prev => [...prev, { id: Date.now(), name: newGroupName.trim(), items: [], subgroups: [] }]);
            setNewGroupName(''); setShowAddGroup(false);
          }
          function delGroup(gId) {
            if (!window.confirm('Ištrinti grupę?')) return;
            setGroups(prev => prev.filter(g => g.id !== gId));
          }
          function saveGroupName(gId) {
            setGroups(prev => prev.map(g => g.id===gId ? {...g, name: editGroupName} : g));
            setEditGroupId(null);
          }

          // Pogrupiai
          function addSubgroup(gId) {
            if (!newSubgroupName.trim()) return;
            setGroups(prev => prev.map(g => g.id===gId ? {...g, subgroups: [...(g.subgroups||[]), { id: Date.now()+Math.random(), name: newSubgroupName.trim(), items: [] }]} : g));
            setNewSubgroupName(''); setShowAddSubgroupFor(null);
          }
          function delSubgroup(gId, sgId) {
            if (!window.confirm('Ištrinti pogrupį? Jo pozicijos bus ištrintos.')) return;
            setGroups(prev => prev.map(g => g.id===gId ? {...g, subgroups: (g.subgroups||[]).filter(sg=>sg.id!==sgId)} : g));
          }
          function saveSubgroupName(gId, sgId) {
            setGroups(prev => prev.map(g => g.id===gId ? {...g, subgroups: (g.subgroups||[]).map(sg=>sg.id===sgId?{...sg,name:editSubgroupName}:sg)} : g));
            setEditSubgroupLoc(null);
          }

          function addItem(gId, sgId) {
            const it = { ...newItem, id: Date.now(),
              qty: parseFloat(newItem.qty)||1,
              priceEx: parseFloat(newItem.priceEx)||0,
              pvmPct: parseFloat(newItem.pvmPct)||21,
              pvmAmt: parseFloat(newItem.pvmAmt)||0,
              priceInc: parseFloat(newItem.priceInc)||0,
              discount: parseFloat(newItem.discount)||0,
              personalPrice: parseFloat(newItem.personalPrice)||0,
              personalDiscount: parseFloat(newItem.personalDiscount)||0 };
            setGroups(prev => withItemsArray(prev, gId, sgId, items => [...items, it]));
            rememberManufacturer(newItem.gamintojas);
            setNewItem(emptyItem()); setShowAddItem(null);
          }
          function startEditItem(it, gId, sgId) {
            setEditItemId(it.id);
            setEditItemLoc({gId, sgId});
            setEditItem({...it, qty: it.qty||1, priceEx: it.priceEx||'', pvmAmt: it.pvmAmt||'', priceInc: it.priceInc||'', discount: it.discount||'', personalPrice: it.personalPrice||'', personalDiscount: it.personalDiscount||''});
          }
          function saveItem() {
            if (!editItemLoc) return;
            const it = {...editItem, qty: parseFloat(editItem.qty)||1, priceEx: parseFloat(editItem.priceEx)||0, pvmPct: parseFloat(editItem.pvmPct)||21, pvmAmt: parseFloat(editItem.pvmAmt)||0, priceInc: parseFloat(editItem.priceInc)||0, discount: parseFloat(editItem.discount)||0, personalPrice: parseFloat(editItem.personalPrice)||0, personalDiscount: parseFloat(editItem.personalDiscount)||0};
            setGroups(prev => withItemsArray(prev, editItemLoc.gId, editItemLoc.sgId, items => items.map(x => x.id===it.id ? it : x)));
            rememberManufacturer(editItem.gamintojas);
            setEditItemId(null); setEditItem(null); setEditItemLoc(null);
          }
          function delItem(gId, sgId, iId) {
            setGroups(prev => withItemsArray(prev, gId, sgId, items => items.filter(x=>x.id!==iId)));
            if (editItemId===iId) { setEditItemId(null); setEditItem(null); setEditItemLoc(null); }
          }

          function applyDiscount(gId, sgId) {
            const key = sgId || gId;
            const disc = parseFloat(groupDiscount[key])||0;
            if (!disc) return;
            setGroups(prev => withItemsArray(prev, gId, sgId, items => items.map(it => {
              const ex = parseFloat(it.priceEx)||0;
              const personal = r2(ex * (1 - disc/100));
              return {...it, discount: disc, personalPrice: personal};
            })));
          }

          // Importas iš .xls/.xlsx — stulpeliai: Grupė | Pogrupis | Pavadinimas | Gamintojas |
          // Vnt. (kiekis) | Mato vnt. | Kaina be PVM | PVM % | Kaina su PVM.
          function cellVal(row, ...colNames) {
            const wanted = colNames.map(c => c.trim().toLowerCase());
            const key = Object.keys(row).find(k => wanted.includes(k.trim().toLowerCase()));
            return key !== undefined ? row[key] : '';
          }

          // Skaičius gali būti su kableliu vietoj dešimtainio taško (LT lokalė), pvz. "2,6".
          function parseNum(v) {
            if (v === '' || v == null) return NaN;
            let s = String(v).trim();
            if (/^-?\d{1,3}(\.\d{3})*,\d+$/.test(s)) s = s.replace(/\./g, '').replace(',', '.');
            else if (/^-?\d+,\d+$/.test(s)) s = s.replace(',', '.');
            return parseFloat(s);
          }

          // Ieško esamos pozicijos pagal grupę/pogrupį/pavadinimą (case-insensitive), kad importas jos nedubliuotų.
          function findExistingItem(groupsList, groupName, subgroupName, itemName) {
            const gnLower = groupName.trim().toLowerCase();
            const inLower = itemName.trim().toLowerCase();
            const group = groupsList.find(g => g.name.trim().toLowerCase() === gnLower);
            if (!group) return null;
            if (subgroupName) {
              const snLower = subgroupName.trim().toLowerCase();
              const sg = (group.subgroups||[]).find(s => s.name.trim().toLowerCase() === snLower);
              if (!sg) return null;
              const found = (sg.items||[]).find(it => it.name.trim().toLowerCase() === inLower);
              return found ? { groupId: group.id, subgroupId: sg.id, item: found } : null;
            }
            const found = (group.items||[]).find(it => it.name.trim().toLowerCase() === inLower);
            return found ? { groupId: group.id, subgroupId: null, item: found } : null;
          }

          async function handleImportFile(e) {
            const file = e.target.files[0];
            if (!file) return;
            e.target.value = '';
            setImporting(true);
            try {
              const buf = await file.arrayBuffer();
              const wb = XLSX.read(buf, { type: 'array' });
              const sheet = wb.Sheets[wb.SheetNames[0]];
              const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

              if (rows.length === 0) { alert('Faile nerasta duomenų eilučių.'); return; }

              const byGroup = new Map(); // grupės pavadinimas -> Map(pogrupio pavadinimas -> pozicijų masyvas)
              const priceChanges = []; // pozicijos, kurios jau egzistuoja, bet kaina faile skiriasi
              let skipped = 0, unchanged = 0;
              rows.forEach(row => {
                const groupName = String(cellVal(row, 'Grupė')).trim();
                // Pozicijos visada importuojasi į pogrupį — jei stulpelis tuščias, naudojamas "Bendra".
                const subgroupName = String(cellVal(row, 'Pogrupis')).trim() || 'Bendra';
                const name = String(cellVal(row, 'Pavadinimas')).trim();
                if (!groupName || !name) { skipped++; return; }
                const gamintojas = String(cellVal(row, 'Gamintojas')).trim();
                const unit = String(cellVal(row, 'Mato vnt.')).trim() || 'vnt.';
                const qty = parseNum(cellVal(row, 'Vnt.', 'kiekis')) || 1;
                const pvmPct = parseNum(cellVal(row, 'PVM %', 'PVM')) || 21;
                const priceExRaw = parseNum(cellVal(row, 'Kaina be PVM'));
                const priceIncRaw = parseNum(cellVal(row, 'Kaina su PVM'));
                const hasEx = Number.isFinite(priceExRaw);
                const hasInc = Number.isFinite(priceIncRaw);
                let priceEx, priceInc;
                if (hasEx && hasInc) {
                  // Abi kainos nurodytos — pasitikima faile esančiomis reikšmėmis.
                  priceEx = r2(priceExRaw);
                  priceInc = r2(priceIncRaw);
                } else if (hasInc) {
                  // Nurodyta tik kaina su PVM — kaina be PVM išskaičiuojama.
                  priceInc = r2(priceIncRaw);
                  priceEx = r2(priceInc / (1 + pvmPct/100));
                } else {
                  // Nurodyta tik kaina be PVM (arba nieko) — kaina su PVM apskaičiuojama.
                  priceEx = r2(priceExRaw || 0);
                  priceInc = r2(priceEx * (1 + pvmPct/100));
                }
                const pvmAmt = r2(priceInc - priceEx);

                // Jei tokia pozicija (grupė+pogrupis+pavadinimas) jau egzistuoja — nedubliuojame.
                const existing = findExistingItem(groups, groupName, subgroupName, name);
                if (existing) {
                  const oldEx = r2(parseFloat(existing.item.priceEx)||0);
                  if (Math.abs(oldEx - priceEx) < 0.005) {
                    unchanged++;
                  } else {
                    priceChanges.push({
                      groupId: existing.groupId, subgroupId: existing.subgroupId, itemId: existing.item.id,
                      name, oldPriceEx: oldEx, newPriceEx: priceEx, pvmPct, pvmAmt, priceInc,
                    });
                  }
                  rememberManufacturer(gamintojas);
                  return;
                }

                const item = {
                  id: Date.now() + Math.random(),
                  name, gamintojas, unit, qty,
                  priceEx, pvmPct, pvmAmt, priceInc,
                  priceType: 'client', // pagal nutylėjimą — kliento kaina
                  discount: 0, personalPrice: 0, personalDiscount: 0, // nuolaida iš anksto nenustatoma
                };
                if (!byGroup.has(groupName)) byGroup.set(groupName, new Map());
                const subgroupsMap = byGroup.get(groupName);
                if (!subgroupsMap.has(subgroupName)) subgroupsMap.set(subgroupName, []);
                subgroupsMap.get(subgroupName).push(item);
                rememberManufacturer(gamintojas);
              });

              if (skipped === rows.length) {
                alert('Faile nerasta tinkamų eilučių — patikrinkite, ar yra stulpeliai "Grupė" ir "Pavadinimas".');
                return;
              }

              let newCount = 0;
              if (byGroup.size > 0) {
                setGroups(prev => {
                  const next = prev.map(g => ({...g, items: [...(g.items||[])], subgroups: (g.subgroups||[]).map(sg=>({...sg, items:[...(sg.items||[])]}))}));
                  byGroup.forEach((subgroupsMap, groupName) => {
                    let group = next.find(g => g.name.trim().toLowerCase() === groupName.trim().toLowerCase());
                    if (!group) {
                      group = { id: Date.now() + Math.random(), name: groupName, items: [], subgroups: [] };
                      next.push(group);
                    }
                    subgroupsMap.forEach((items, subgroupName) => {
                      let sg = (group.subgroups||[]).find(s => s.name.trim().toLowerCase() === subgroupName.trim().toLowerCase());
                      if (!sg) {
                        sg = { id: Date.now() + Math.random(), name: subgroupName, items: [] };
                        group.subgroups = [...(group.subgroups||[]), sg];
                      }
                      sg.items.push(...items);
                    });
                  });
                  return next;
                });
                byGroup.forEach(subgroupsMap => {
                  subgroupsMap.forEach(items => { newCount += items.length; });
                });
              }

              let pricesApplied = false;
              if (priceChanges.length > 0) {
                const lines = priceChanges.map(pc => {
                  const dir = pc.newPriceEx > pc.oldPriceEx ? 'padidėjo' : 'sumažėjo';
                  return `• ${pc.name}: ${pc.oldPriceEx.toFixed(2)} € → ${pc.newPriceEx.toFixed(2)} € (kaina ${dir})`;
                });
                pricesApplied = window.confirm(
                  `Rasta ${priceChanges.length} pozicijų, kurios kainininke jau yra, bet faile jų kaina skiriasi:\n\n` +
                  lines.join('\n') +
                  `\n\nAtnaujinti šias kainas pagal importuojamą failą?`
                );
                if (pricesApplied) {
                  setGroups(prev => prev.map(g => ({
                    ...g,
                    items: (g.items||[]).map(it => {
                      const pc = priceChanges.find(p => p.groupId===g.id && p.subgroupId===null && p.itemId===it.id);
                      return pc ? {...it, priceEx: pc.newPriceEx, pvmPct: pc.pvmPct, pvmAmt: pc.pvmAmt, priceInc: pc.priceInc} : it;
                    }),
                    subgroups: (g.subgroups||[]).map(sg => ({
                      ...sg,
                      items: (sg.items||[]).map(it => {
                        const pc = priceChanges.find(p => p.groupId===g.id && p.subgroupId===sg.id && p.itemId===it.id);
                        return pc ? {...it, priceEx: pc.newPriceEx, pvmPct: pc.pvmPct, pvmAmt: pc.pvmAmt, priceInc: pc.priceInc} : it;
                      })
                    }))
                  })));
                }
              }

              let msg = `Importuota ${newCount} naujų pozicijų.`;
              if (priceChanges.length > 0) msg += ` ${priceChanges.length} pozicijų kaina faile skyrėsi${pricesApplied ? ' — atnaujinta.' : ' — palikta nepakeista.'}`;
              if (unchanged > 0) msg += ` ${unchanged} pozicijų kaina nepasikeitusi — praleista (nedubliuota).`;
              if (skipped > 0) msg += ` Praleista ${skipped} eilučių (trūko "Grupė" arba "Pavadinimas").`;
              alert(msg);
            } catch (err) {
              console.error('XLS import error:', err);
              alert('Nepavyko nuskaityti failo. Patikrinkite, ar tai .xls/.xlsx failas su reikiamais stulpeliais.');
            } finally {
              setImporting(false);
            }
          }

          const inp = {border:'1px solid #e5e7eb',borderRadius:5,padding:'4px 6px',fontSize:11,boxSizing:'border-box',width:'100%'};

          // Pakartotinai naudojama pozicijų lentelė — tiek grupės viršuje, tiek pogrupiuose.
          function renderItemsTable(items, gId, sgId) {
            const isAdding = showAddItem && showAddItem.gId===gId && showAddItem.sgId===sgId;
            return (
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',fontSize:11,borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{background:'#f0f0f2',color:'#6b7280'}}>
                      <th style={{padding:'5px 8px',textAlign:'left',fontWeight:600}}>Pavadinimas</th>
                      <th style={{padding:'5px 8px',textAlign:'left',fontWeight:600}}>Gamintojas</th>
                      <th style={{padding:'5px 4px',textAlign:'center',fontWeight:600}}>Mato vnt.</th>
                      <th style={{padding:'5px 4px',textAlign:'center',fontWeight:600}}>Vnt.</th>
                      <th style={{padding:'5px 8px',textAlign:'right',fontWeight:600}}>Kaina be PVM</th>
                      <th style={{padding:'5px 4px',textAlign:'right',fontWeight:600}}>PVM %</th>
                      <th style={{padding:'5px 8px',textAlign:'right',fontWeight:600}}>PVM €</th>
                      <th style={{padding:'5px 8px',textAlign:'right',fontWeight:600}}>Kaina su PVM</th>
                      <th style={{padding:'5px 8px',textAlign:'center',fontWeight:600}}>Tipas</th>
                      <th style={{padding:'5px 4px',textAlign:'right',fontWeight:600}}>Nuolaida %</th>
                      <th style={{padding:'5px 8px',textAlign:'right',fontWeight:600,color:'#15803d'}}>Asmeninė kaina</th>
                      <th style={{padding:'5px 4px',textAlign:'center',fontWeight:600}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      editItemId === it.id ? (
                        <ItemForm key={it.id} item={editItem}
                          onChange={handleEditItemChange}
                          onSave={saveItem}
                          onCancel={()=>{setEditItemId(null);setEditItem(null);setEditItemLoc(null);}}
                          gId={gId} knownManufacturers={knownManufacturers}/>
                      ) : (
                        <tr key={it.id} style={{background:idx%2===0?'#fff':'#fafafa',borderBottom:'1px solid #f0f0f0'}}>
                          <td style={{padding:'6px 8px',fontWeight:600,color:'#1c1c1c'}}>{it.name}</td>
                          <td style={{padding:'6px 8px',color:'#6b7280'}}>{it.gamintojas||'—'}</td>
                          <td style={{padding:'6px 4px',textAlign:'center',color:'#6b7280'}}>{it.unit}</td>
                          <td style={{padding:'6px 4px',textAlign:'center',color:'#6b7280'}}>{it.qty||1}</td>
                          <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600}}>{(parseFloat(it.priceEx)||0).toFixed(2)} €</td>
                          <td style={{padding:'6px 4px',textAlign:'right',color:'#9ca3af'}}>{it.pvmPct||21}%</td>
                          <td style={{padding:'6px 8px',textAlign:'right',color:'#9ca3af'}}>{(parseFloat(it.pvmAmt)||0).toFixed(2)} €</td>
                          <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600}}>{(parseFloat(it.priceInc)||0).toFixed(2)} €</td>
                          <td style={{padding:'6px 8px',textAlign:'center'}}>
                            <span style={{fontSize:10,padding:'2px 6px',borderRadius:10,background:it.priceType==='personal'?'#dcfce7':'#e0f2fe',color:it.priceType==='personal'?'#15803d':'#0369a1',fontWeight:700}}>
                              {it.priceType==='personal'?'Asmeninė':'Kliento'}
                            </span>
                          </td>
                          <td style={{padding:'6px 4px',textAlign:'right',color:'#ea580c',fontWeight:600}}>{parseFloat(it.discount)||0}%</td>
                          <td style={{padding:'6px 8px',textAlign:'right',color:'#15803d',fontWeight:700}}>{(parseFloat(it.personalPrice)||0).toFixed(2)} €</td>
                          <td style={{padding:'6px 4px',textAlign:'center'}}>
                            <div style={{display:'flex',gap:3}}>
                              <button onClick={()=>startEditItem(it, gId, sgId)} style={{background:'none',border:'none',color:'#9ca3af',cursor:'pointer',fontSize:13}}>✏</button>
                              <button onClick={()=>delItem(gId,sgId,it.id)} style={{background:'none',border:'none',color:'#d1d5db',cursor:'pointer',fontSize:15}}>×</button>
                            </div>
                          </td>
                        </tr>
                      )
                    ))}
                    {/* Add item row */}
                    {isAdding ? (
                      <ItemForm item={newItem}
                        onChange={handleNewItemChange}
                        onSave={()=>addItem(gId, sgId)}
                        onCancel={()=>{setShowAddItem(null);setNewItem(emptyItem());}}
                        gId={gId} knownManufacturers={knownManufacturers}/>
                    ) : (
                      <tr>
                        <td colSpan="12" style={{padding:'6px 8px'}}>
                          <button onClick={()=>{setShowAddItem({gId,sgId});setNewItem(emptyItem());}}
                            style={{background:'none',border:'1px dashed #d1d5db',borderRadius:5,padding:'4px 12px',fontSize:11,color:'#9ca3af',cursor:'pointer',width:'100%'}}>
                            + Pridėti poziciją
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            );
          }

          return (
            <div>
              {/* Kainininko antraštė */}
              <div style={{background:'#fff',borderRadius:10,padding:'12px 16px',marginBottom:12,display:'flex',alignItems:'center',gap:10,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                {editingName ? (
                  <div style={{display:'flex',gap:6,flex:1}}>
                    <input autoFocus value={nameVal} onChange={e=>setNameVal(e.target.value)}
                      onKeyDown={e=>{if(e.key==='Enter'){onRename(nameVal);setEditingName(false);}if(e.key==='Escape')setEditingName(false);}}
                      style={{flex:1,border:'1px solid #e5e7eb',borderRadius:5,padding:'5px 8px',fontSize:14,fontWeight:700}}/>
                    <button onClick={()=>{onRename(nameVal);setEditingName(false);}} style={{background:'#383853',color:'#fff',border:'none',borderRadius:5,padding:'5px 10px',fontSize:12,fontWeight:700,cursor:'pointer'}}>✓</button>
                    <button onClick={()=>setEditingName(false)} style={{background:'#f3f4f6',border:'none',borderRadius:5,padding:'5px 8px',fontSize:12,cursor:'pointer'}}>✕</button>
                  </div>
                ) : (
                  <div style={{display:'flex',alignItems:'center',gap:8,flex:1}}>
                    <span style={{fontSize:16,fontWeight:800,color:'#1c1c1c'}}>{kainininkas.name}</span>
                    <button onClick={()=>{setNameVal(kainininkas.name);setEditingName(true);}} style={{background:'none',border:'none',color:'#9ca3af',cursor:'pointer',fontSize:14}}>✏</button>
                  </div>
                )}
                <span style={{fontSize:12,color:'#9ca3af'}}>{groups.reduce((a,g)=>a+(g.items||[]).length+(g.subgroups||[]).reduce((x,sg)=>x+(sg.items||[]).length,0),0)} pozicijų</span>
                <button onClick={()=>setShowManufacturers(v=>!v)} title="Gamintojų sąrašas"
                  style={{background:showManufacturers?'#383853':'#f3f4f6',color:showManufacturers?'#fff':'#383853',border:'1px solid #e5e7eb',borderRadius:6,padding:'4px 10px',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                  🏭 Gamintojai ({knownManufacturers.length})
                </button>
                <input ref={importFileRef} type="file" accept=".xls,.xlsx" style={{display:'none'}} onChange={handleImportFile}/>
                <button disabled={importing} onClick={()=>importFileRef.current&&importFileRef.current.click()}
                  title="Importuoti prekes iš .xls/.xlsx failo (stulpeliai: Grupė, Pogrupis, Pavadinimas, Gamintojas, Vnt. (kiekis), Mato vnt., Kaina be PVM, PVM %, Kaina su PVM). Jau esančios pozicijos nedubliuojamos — jei kaina faile pasikeitusi, paklausiama, ar atnaujinti."
                  style={{background:'#f3f4f6',color:'#383853',border:'1px solid #e5e7eb',borderRadius:6,padding:'4px 10px',fontSize:11,fontWeight:700,
                    cursor:importing?'default':'pointer',opacity:importing?0.6:1}}>
                  {importing ? '⏳ Importuojama...' : '📥 Importuoti .xls'}
                </button>
                <button onClick={()=>setShowAddGroup(v=>!v)}
                  style={{background:'#735DFF',color:'#fff',border:'none',borderRadius:6,padding:'5px 12px',fontSize:12,fontWeight:700,cursor:'pointer'}}>+ Grupė</button>
              </div>

              {/* Gamintojų valdymo skydelis */}
              {showManufacturers && (
                <div style={{background:'#f8f9fb',border:'1px solid #e5e7eb',borderRadius:10,padding:'12px',marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#374151',marginBottom:8}}>🏭 Gamintojų sąrašas</div>
                  {knownManufacturers.length === 0 && (
                    <div style={{fontSize:11,color:'#9ca3af',marginBottom:8}}>Nėra gamintojų. Pridėkite arba įkelsite automatiškai pridedant pozicijas.</div>
                  )}
                  <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:8}}>
                    {knownManufacturers.map(g => (
                      <div key={g} style={{display:'flex',alignItems:'center',gap:3,background:'#fff',border:'1px solid #e5e7eb',borderRadius:6,padding:'3px 8px'}}>
                        <span style={{fontSize:11,fontWeight:600,color:'#374151'}}>{g}</span>
                        <button onClick={()=>delManufacturer(g)} title={`Šalinti ${g}`}
                          style={{background:'none',border:'none',cursor:'pointer',color:'#d1d5db',fontSize:13,padding:'0 1px',lineHeight:1}}>×</button>
                      </div>
                    ))}
                  </div>
                  {/* Pridėti naują gamintoją */}
                  <div style={{display:'flex',gap:6}}>
                    <input value={newManufacturer} onChange={e=>setNewManufacturer(e.target.value)}
                      onKeyDown={e=>{if(e.key==='Enter'&&newManufacturer.trim()){setManufacturers(prev=>[...new Set([...prev,newManufacturer.trim()])].sort());setNewManufacturer('');}}}
                      placeholder="Pridėti gamintoją..."
                      style={{flex:1,border:'1px solid #e5e7eb',borderRadius:6,padding:'5px 8px',fontSize:12}}/>
                    <button onClick={()=>{if(newManufacturer.trim()){setManufacturers(prev=>[...new Set([...prev,newManufacturer.trim()])].sort());setNewManufacturer('');}}}
                      style={{background:'#383853',color:'#fff',border:'none',borderRadius:6,padding:'5px 12px',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                      + Pridėti
                    </button>
                  </div>
                </div>
              )}

              {/* Naujos grupės forma */}
              {showAddGroup && (
                <div style={{background:'#fff8f6',border:'1px solid #fecaca',borderRadius:8,padding:'10px',marginBottom:10,display:'flex',gap:8}}>
                  <input autoFocus value={newGroupName} onChange={e=>setNewGroupName(e.target.value)}
                    onKeyDown={e=>{if(e.key==='Enter')addGroup();if(e.key==='Escape'){setShowAddGroup(false);setNewGroupName('');}}}
                    placeholder="Grupės pavadinimas..."
                    style={{flex:1,border:'1px solid #e5e7eb',borderRadius:5,padding:'6px 8px',fontSize:13}}/>
                  <button onClick={addGroup} style={{background:'#735DFF',color:'#fff',border:'none',borderRadius:6,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer'}}>Pridėti</button>
                  <button onClick={()=>{setShowAddGroup(false);setNewGroupName('');}} style={{background:'#f3f4f6',border:'none',borderRadius:6,padding:'6px 10px',fontSize:12,cursor:'pointer'}}>Atšaukti</button>
                </div>
              )}

              {/* Grupės */}
              {groups.length === 0 && !showAddGroup && (
                <div style={{background:'#fff',borderRadius:10,padding:'40px',textAlign:'center',color:'#9ca3af'}}>
                  <div style={{fontSize:32,marginBottom:8}}>📋</div>
                  <div style={{fontSize:14}}>Kainininkas tuščias. Pridėkite medžiagų grupę.</div>
                </div>
              )}

              {groups.map(grp => {
                const isExp = expanded[grp.id] === true;
                const grpItems = grp.items || [];
                const grpSubgroups = grp.subgroups || [];
                const totalItemCount = grpItems.length + grpSubgroups.reduce((a,sg)=>a+(sg.items||[]).length,0);
                return (
                  <div key={grp.id} style={{background:'#fff',borderRadius:10,marginBottom:10,overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                    {/* Grupės antraštė */}
                    <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'#f8f9fb',borderBottom:'1px solid #e5e7eb',cursor:'pointer'}}
                      onClick={()=>setExpanded(p=>({...p,[grp.id]:!isExp}))}>
                      <span style={{color:'#9ca3af',fontSize:12,width:14,flexShrink:0}}>{isExp?'▼':'▶'}</span>
                      {editGroupId===grp.id ? (
                        <input autoFocus value={editGroupName} onChange={e=>setEditGroupName(e.target.value)}
                          onKeyDown={e=>{if(e.key==='Enter')saveGroupName(grp.id);if(e.key==='Escape')setEditGroupId(null);}}
                          onClick={e=>e.stopPropagation()}
                          style={{flex:1,border:'1px solid #e5e7eb',borderRadius:5,padding:'3px 7px',fontSize:13,fontWeight:700}}/>
                      ) : (
                        <span style={{flex:1,fontSize:13,fontWeight:700,color:'#1c1c1c'}}>{grp.name}</span>
                      )}
                      <span style={{fontSize:11,color:'#9ca3af'}}>{totalItemCount} poz.</span>
                      <div style={{display:'flex',gap:4}} onClick={e=>e.stopPropagation()}>
                        <button onClick={()=>{setEditGroupId(grp.id);setEditGroupName(grp.name);}}
                          style={{background:'none',border:'none',color:'#9ca3af',cursor:'pointer',fontSize:13}}>✏</button>
                        <button onClick={()=>delGroup(grp.id)}
                          style={{background:'none',border:'none',color:'#d1d5db',cursor:'pointer',fontSize:15}}>×</button>
                      </div>
                    </div>

                    {isExp && (
                      <div>
                        {/* Pogrupiai — pozicijos visada gyvena pogrupyje, grupė pati savo lentelės neturi */}
                        <div style={{padding:'8px 12px 12px'}}>
                          {grpSubgroups.map(sg => {
                            const sgExp = expandedSubgroups[sg.id] === true;
                            const sgItems = sg.items || [];
                            return (
                              <div key={sg.id} style={{marginTop:8,border:'1px solid #e5e7eb',borderRadius:8,overflow:'hidden'}}>
                                <div style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'#f8f9fb',cursor:'pointer'}}
                                  onClick={()=>setExpandedSubgroups(p=>({...p,[sg.id]:!sgExp}))}>
                                  <span style={{color:'#9ca3af',fontSize:11,width:12,flexShrink:0}}>{sgExp?'▼':'▶'}</span>
                                  {editSubgroupLoc && editSubgroupLoc.sgId===sg.id ? (
                                    <input autoFocus value={editSubgroupName} onChange={e=>setEditSubgroupName(e.target.value)}
                                      onKeyDown={e=>{if(e.key==='Enter')saveSubgroupName(grp.id,sg.id);if(e.key==='Escape')setEditSubgroupLoc(null);}}
                                      onClick={e=>e.stopPropagation()}
                                      style={{flex:1,border:'1px solid #e5e7eb',borderRadius:5,padding:'2px 6px',fontSize:12,fontWeight:700}}/>
                                  ) : (
                                    <span style={{flex:1,fontSize:12,fontWeight:700,color:'#374151'}}>↳ {sg.name}</span>
                                  )}
                                  <span style={{fontSize:10,color:'#9ca3af'}}>{sgItems.length} poz.</span>
                                  <div style={{display:'flex',alignItems:'center',gap:4}} onClick={e=>e.stopPropagation()}>
                                    <span style={{fontSize:10,color:'#6b7280'}}>Nuolaida %:</span>
                                    <input type="number" step="0.1" value={groupDiscount[sg.id]||''} onChange={e=>setGroupDiscount(p=>({...p,[sg.id]:e.target.value}))}
                                      style={{width:50,border:'1px solid #e5e7eb',borderRadius:5,padding:'2px 4px',fontSize:10}} placeholder="0"/>
                                    <button onClick={()=>applyDiscount(grp.id, sg.id)}
                                      style={{background:'#383853',color:'#fff',border:'none',borderRadius:4,padding:'2px 6px',fontSize:9,fontWeight:700,cursor:'pointer'}}>Taikyti</button>
                                  </div>
                                  <div style={{display:'flex',gap:4}} onClick={e=>e.stopPropagation()}>
                                    <button onClick={()=>{setEditSubgroupLoc({gId:grp.id,sgId:sg.id});setEditSubgroupName(sg.name);}}
                                      style={{background:'none',border:'none',color:'#9ca3af',cursor:'pointer',fontSize:12}}>✏</button>
                                    <button onClick={()=>delSubgroup(grp.id, sg.id)}
                                      style={{background:'none',border:'none',color:'#d1d5db',cursor:'pointer',fontSize:14}}>×</button>
                                  </div>
                                </div>
                                {sgExp && renderItemsTable(sgItems, grp.id, sg.id)}
                              </div>
                            );
                          })}

                          {/* Naujo pogrupio forma */}
                          {showAddSubgroupFor===grp.id ? (
                            <div style={{display:'flex',gap:6,marginTop:8}}>
                              <input autoFocus value={newSubgroupName} onChange={e=>setNewSubgroupName(e.target.value)}
                                onKeyDown={e=>{if(e.key==='Enter')addSubgroup(grp.id);if(e.key==='Escape'){setShowAddSubgroupFor(null);setNewSubgroupName('');}}}
                                placeholder="Pogrupio pavadinimas..."
                                style={{flex:1,border:'1px solid #e5e7eb',borderRadius:5,padding:'5px 8px',fontSize:12}}/>
                              <button onClick={()=>addSubgroup(grp.id)} style={{background:'#735DFF',color:'#fff',border:'none',borderRadius:6,padding:'5px 12px',fontSize:11,fontWeight:700,cursor:'pointer'}}>Pridėti</button>
                              <button onClick={()=>{setShowAddSubgroupFor(null);setNewSubgroupName('');}} style={{background:'#f3f4f6',border:'none',borderRadius:6,padding:'5px 8px',fontSize:11,cursor:'pointer'}}>Atšaukti</button>
                            </div>
                          ) : (
                            <button onClick={()=>{setShowAddSubgroupFor(grp.id);setNewSubgroupName('');}}
                              style={{marginTop:8,background:'none',border:'1px dashed #d1d5db',borderRadius:6,padding:'5px 10px',fontSize:11,color:'#9ca3af',cursor:'pointer'}}>
                              + Pogrupis
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }
